import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { SyncLoader } from "react-spinners";
import { toast } from "sonner";
import ArrowBackIcon from "../../../../assets/images/arrow_back_black_icon.svg?react";
import ThemeConstants from "../../../../config/theme/theme_constants";
import { UserInterface } from "../../../../core/components/user/domain/interfaces/user_interface";
import { useGetCertificate } from "../../domain/hooks/use_get_certificate";
import PdfPreview from "../components/pdf_preview";
import { AxiosError } from "axios";
import { CareerStatusEnum } from "../../../../core/enum/career_status_enum";
import { Trans } from "react-i18next";
import { useTranslation } from "../../../../i18n/useTranslation";

const CertificatePage: React.FC = () => {
  const { t } = useTranslation();
  const theme = ThemeConstants();
  const navigate = useNavigate();
  const { themeId } = useParams();
  const { firstname, careerStatus } = useOutletContext<UserInterface>();
  const { data: certificate, isPending, error } = useGetCertificate(themeId);

  useEffect(() => {
    if (careerStatus === CareerStatusEnum.Start) {
      navigate('/career/start', { replace: true });
    }
  }, [careerStatus, navigate]);

  const meta = useMemo(() => {
    if (!certificate?.picture) return undefined;
    return {
      url: certificate.picture,
      filename: t('course.certificate.filename', { serial: certificate.serialNumber }),
    };
  }, [certificate, t]);

  useEffect(() => {
    if (error) {
      const axiosError = error as AxiosError;
      const data = axiosError.response?.data as { message?: string } | undefined;
      const message = data?.message || "An error occurred";
      if (axiosError.response?.status === 404 || message === "themeId must be a valid MongoDB ObjectId") {
        toast.error(t('course.certificate.not_found'));
        navigate('/career', { replace: true });
      }
    }
  }, [error, navigate, t]);

  const handleDownload = useCallback(async () => {
    if (!meta) return;
    try {
      const response = await fetch(meta.url);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = meta.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(meta.url, "_blank", "noopener,noreferrer");
    }
  }, [meta]);

  const handleShare = useCallback(async () => {
    if (!meta) return;

    let blob: Blob;
    try {
      const response = await fetch(meta.url);
      if (!response.ok) throw new Error("fetch failed");
      blob = await response.blob();
    } catch {
      toast.error(t('course.certificate.fetch_error'));
      return;
    }

    const file = new File([blob], meta.filename, { type: "application/pdf" });
    const canShareFile =
      typeof navigator !== "undefined" &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files: [file] });

    if (canShareFile && typeof navigator.share === "function") {
      try {
        await navigator.share({
          files: [file],
          title: t('course.certificate.share_title'),
          text: t('course.certificate.share_text'),
        });
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          toast.error(t('course.certificate.share_failed'));
        }
      }
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = meta.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
    toast.info(t('course.certificate.share_unsupported'));
  }, [meta, t]);

  const handleFinish = useCallback(() => {
    navigate("/career");
  }, [navigate]);

  return (
    <div className="min-h-dvh pt-[5.6dvh] max-lg:px-[3%] lg:px-[1%] flex flex-col items-center gap-10 relative pb-12">
      <button
        className="absolute top-8 left-5 cursor-pointer w-6 h-6 flex items-center justify-center"
        onClick={() => navigate(-1)}
      >
        <ArrowBackIcon color="black" className="w-4.25 h-auto" />
      </button>

      <div className="w-full max-w-150 flex flex-col items-center gap-8 pt-4">
        <div className="w-full bg-white rounded-2xl shadow-md overflow-hidden">
          {isPending ? (
            <div className="aspect-4/3 flex items-center justify-center">
              <SyncLoader color={theme.UvibesPink} size={20} speedMultiplier={0.5} />
            </div>
          ) : meta ? (
            <PdfPreview url={meta.url} />
          ) : (
            <div className="aspect-4/3 flex items-center justify-center px-6 text-center">
              <span className="text-sm" style={{ color: theme.UvibesGray }}>
                {t('course.certificate.preview_unavailable')}
              </span>
            </div>
          )}
        </div>

        <h1
          className="text-3xl font-extrabold text-center leading-tight"
          style={{ color: theme.UvibesPink }}
        >
          {t('course.certificate.congrats', { firstname })}
        </h1>

        <div className="text-6xl" role="img" aria-label={t('course.certificate.party_alt')}>
          🎉
        </div>

        <p
          className="text-sm font-semibold text-center leading-relaxed"
          style={{ color: theme.UvibesPink }}
        >
          <Trans
            i18nKey="course.certificate.validated"
            components={{ verify: <a href="https://uvibes.fr/verification-attestation" target="_blank" rel="noopener noreferrer" className="underline wrap-break-word" /> }}
          />
          <br/>
          {t('course.certificate.keep_reminder')}
        </p>

        <div className="flex flex-col gap-3 w-full max-w-80 pt-2">
          <button
            type="button"
            onClick={handleDownload}
            disabled={!meta}
            className="w-full py-3 rounded-full text-white font-bold cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            style={{ backgroundColor: theme.UvibesPink }}
          >
            {t('course.certificate.download')}
          </button>
          <button
            type="button"
            onClick={handleShare}
            disabled={!meta}
            className="w-full py-3 rounded-full bg-white font-bold cursor-pointer border-2 disabled:cursor-not-allowed disabled:opacity-50"
            style={{ color: theme.UvibesPink, borderColor: theme.UvibesPink }}
          >
            {t('course.certificate.share')}
          </button>
          <button
            type="button"
            onClick={handleFinish}
            className="w-full py-3 rounded-full bg-white font-bold cursor-pointer border-2"
            style={{ color: theme.UvibesPink, borderColor: theme.UvibesPink }}
          >
            {t('common.button.finish')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CertificatePage;
